import type { ComponentType } from 'react';
import React from 'react';

import type {
  ColorPaletteManager,
  DebugMode,
  GeneralContext,
  Platform,
  RicosPortal,
  RicosServices,
  RicosTheme,
  TabFocusBehavior,
  TFunction,
} from './../ricos-types/index.js';
import type { InteractionType } from './../_shared/use-user-interaction.js';

type RicosContextType = GeneralContext & { theme: RicosTheme; colorManager?: ColorPaletteManager };

export const RicosContext = React.createContext<RicosContextType>({
  locale: 'en',
  localeContent: 'en',
  experiments: {},
  isMobile: false,
  t: ((text: string) => text) as TFunction,
  languageDir: 'ltr',
  theme: {} as RicosTheme,
  portal: null,
  debugMode: [],
  platform: 'macOs',
  content: null as unknown as RicosServices['content'],
  jsdomMode: false,
  environment: 'liveSite',
  tabIndex: 0,
  lastInteraction: null,
  tabFocusBehavior: 'edit',
});

export const RicosContextProvider = ({
  children,
  isMobile,
  locale,
  localeContent,
  experiments,
  languageDir,
  theme,
  t,
  portal,
  debugMode = [] as DebugMode[],
  jsdomMode = false,
  platform = 'macOs' as Platform,
  content = null as unknown as RicosServices['content'],
  environment = 'liveSite',
  tabIndex = 0,
  lastInteraction = null,
  tabFocusBehavior = 'edit',
  colorManager,
}: {
  children: React.ReactNode;
  isMobile: boolean;
  locale: string;
  localeContent: string;
  experiments?: GeneralContext['experiments'];
  languageDir: GeneralContext['languageDir'];
  theme: RicosTheme;
  t: TFunction;
  portal?: RicosPortal;
  debugMode?: DebugMode[];
  jsdomMode?: boolean;
  platform?: Platform;
  content?: RicosServices['content'];
  environment?: GeneralContext['environment'];
  tabIndex?: number;
  lastInteraction?: InteractionType;
  tabFocusBehavior?: TabFocusBehavior;
  colorManager?: ColorPaletteManager;
}) => {
  return (
    <RicosContext.Provider
      value={{
        t,
        locale,
        localeContent,
        isMobile,
        experiments: experiments ?? {},
        languageDir,
        theme,
        portal: portal ?? null,
        debugMode,
        jsdomMode,
        platform,
        content,
        environment,
        tabIndex,
        lastInteraction,
        tabFocusBehavior,
        colorManager,
      }}
    >
      {children}
    </RicosContext.Provider>
  );
};

export function withRicosContext<T = any>(Component: ComponentType<T>) {
  return (props: T) => {
    return (
      <RicosContext.Consumer>
        {(value: GeneralContext) => <Component {...props} ricosContext={value} />}
      </RicosContext.Consumer>
    );
  };
}

export const RicosContextConsumer = ({ children }: $TSFixMe) => {
  return (
    <RicosContext.Consumer>{(value: GeneralContext) => children(value)}</RicosContext.Consumer>
  );
};
